import { Fragment, useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { PARTIAL_NOTE, WITHHELD, partialOf, sumShown } from '../api/answer'
import { Withheld, markPartial } from './Withheld'
import type { Api } from '../api'
import { ApiError, NO_RECV_FILTERS, USD, isConverted } from '../api/types'
import type { ApiFailure, Currency, MatrixLevel, MatrixNode, MatrixPath, RecvFilters } from '../api/types'
import { Loading, money, currencyNote } from './ui'
import { Explain, ExplainMark } from './DevModeContext'
import { FailureState } from './FailureState'
import type { ExportDoc, ExportRow } from '../export/model'
import { useSortMirror } from './gridSort'
import { GridChrome, ResizeHandle, useGridLayout } from './gridLayout'

/**
 * THE RECEIVABLES MATRIX — the Power BI report's main page, rebuilt.
 *
 * Everything below is taken from the report definition itself
 * (Receivables Dashboard.pbix, page "Receivables Dashboard"), not from memory:
 *
 *   ROWS      Business Unit -> Business Team -> Salesperson -> Customer -> Document
 *   VALUES    the eleven measures named in COLUMNS below, in this order, with
 *             the display names from the report's own columnProperties
 *   LAYOUT    Compact (stepped), 20px indentation per level
 *   STYLE     NOT copied. The structure, hierarchy, measures, order and sort
 *             above are the report's. The SURFACE is the platform's own design
 *             system. Copying Power BI's chrome was tried on 4 Aug 2026 and
 *             rejected: it made an institutional platform look like a
 *             spreadsheet export, which is the opposite of what this data
 *             deserves.
 *   SORT      Sum(61 To 90 Days) descending
 *   FILTER    open items only (the report-level filter on Trx_Close_Date)
 *
 * ---------------------------------------------------------------------------
 * TWO THINGS THAT ARE NOT LIKE THE POWER BI VERSION, BOTH DELIBERATE
 *
 * 1. The hierarchy is whatever the SERVER says it is. Salesperson_Name is gated
 *    on SLS_Mod; a caller without it drills from team straight to customer, and
 *    this component neither knows nor cares why. Hard-coding the five levels
 *    here would put a security decision in a place anyone can edit.
 *
 * 2. A masked measure renders as an em dash, never as 0. The database masks the
 *    column before the SUM, so null means "you were not shown this" — and a
 *    zero in a receivables column is a number a finance user will act on.
 */

/**
 * The eleven measures, in the report's order, with the report's display names.
 *
 * `width` is set only where the report itself pins one. Its columnHeaders object
 * has autoSizeColumnWidth = true, so the seven entries in its columnWidth array
 * are columns somebody dragged to a chosen width — those are reproduced; the
 * rest auto-size, exactly as they do in Power BI.
 */
type MatrixColumn = { key: keyof MatrixNode; header: string; width?: number }
/** What MatrixRow needs from the layout — the same api the header uses. */
type MatrixLayout = ReturnType<typeof useGridLayout<MatrixColumn>>

const GRID_ID = 'receivables-matrix'

const COLUMNS: MatrixColumn[] = [
  { key: 'advancePayment', header: 'Advance Payment' },
  { key: 'totalReceivables', header: 'Total Receivables' },
  { key: 'totalOverdue', header: 'Total Overdue' },
  { key: 'over30DaysOverdue', header: 'Over 30 Days (Overdue)' },
  { key: 'd0To30', header: '0 To 30 Days', width: 141 },
  { key: 'd31To60', header: '31 To 60 Days', width: 145 },
  { key: 'd61To90', header: '61 To 90 Days', width: 148 },
  { key: 'd91To120', header: '91 To 120 Days', width: 160 },
  { key: 'd121To180', header: '121 To 180 Days', width: 161 },
  { key: 'd181To365', header: '181 To 365 Days', width: 161 },
  { key: 'dOver365', header: 'Over 365 Days', width: 175 },
]

/** The report's own sort: Sum(61 To 90 Days), descending. */
const DEFAULT_SORT: keyof MatrixNode = 'd61To90'

/** Compact layout, 20px per level — the value in the report's rowHeaders object. */
const INDENT_PX = 20

/**
 * TYPE SIZES, CONVERTED FROM THE REPORT'S OWN VALUES.
 *
 * Power BI stores fontSize in POINTS, not pixels, and renders at 96 DPI — so
 * 1pt = 4/3 px. The report sets rowHeaders and columnHeaders to 13pt and leaves
 * values on the theme's `label` class, which CY24SU10.json defines as 10pt
 * Segoe UI.
 *
 * Rendering all three at 13px, as this file first did, made the headers two
 * points too small and the values three points too large — so the size
 * relationship between header and figure was inverted relative to the report
 * being replaced. The numbers are the interface here; getting their weight
 * wrong relative to the labels changes what the eye reads first.
 */
const HEADER_PX = 17 // 13pt
/**
 * An INTEGER line box for the 17px cells, set explicitly. The default 1.5 gives
 * 25.5px, which made every row 42.5px tall — so alternate rows sat at different
 * sub-pixel phases, and the sticky row-header cell (its own compositor layer,
 * snapped to whole device pixels) painted over the separator on every other
 * row. Measured under GPU compositing: rows 42.50px at tops .89 / .39 / .89.
 * 26px makes the row 43px and the phase identical for every row.
 */
const HEADER_LH = '26px'
const VALUE_PX = 13 // 10pt, the theme's label class

const HIDDEN = '—' // em dash: "you were not shown this"

/** What the row-header column is called at each level. */
const LEVEL_CAPTION: Record<MatrixLevel, string> = {
  BusinessUnit: 'Business Unit',
  BusinessTeam: 'Business Team',
  Salesperson: 'Salesperson',
  Customer: 'Customer',
  Document: 'Document',
}

interface Row {
  node: MatrixNode
  path: MatrixPath
  /**
   * The EXPANSION id — pathId of this node's children. Correct for a node that
   * can expand; for a Document leaf childPath returns the parent unchanged, so
   * this is NOT unique across sibling documents. Use it only for expand/toggle,
   * never as the React key. See `key`.
   */
  id: string
  /**
   * The React KEY — globally unique per rendered row.
   *
   * `id` alone is NOT: a customer's document children all share the customer's
   * pathId (childPath treats Document as a leaf), so keying rows by `id` gave
   * every document under one customer the same key. React then reconciled a
   * list of duplicate keys against the real DOM and, on the wrong drill/collapse
   * pattern, threw "Failed to execute 'removeChild' on 'Node'" — an uncaught
   * render error that blanked the whole app. This ties the key to the node's own
   * identity plus its position, so no two rendered rows can ever collide.
   */
  key: string
  depth: number
  /** False when childPath could not advance, so this row is a leaf whatever
      hasChildren claims. Gates both the chevron and toggle. */
  expandable: boolean
}

/** The path that addresses this node's CHILDREN. */
function childPath(parent: MatrixPath, node: MatrixNode): MatrixPath {
  const key = node.nodeKey ?? ''
  switch (node.groupedBy) {
    case 'BusinessUnit':
      return { ...parent, bu: key }
    case 'BusinessTeam':
      return { ...parent, bt: key }
    case 'Salesperson':
      return { ...parent, sp: key }
    case 'Customer':
      return { ...parent, customer: key }
    default:
      return parent // Document is a leaf
  }
}

function pathId(p: MatrixPath): string {
  // U+001F UNIT SEPARATOR between the parts, written as an escape so it is
  // VISIBLE in the source. These are customer and salesperson names from live
  // data, so any printable delimiter can occur inside a value -- and two
  // branches sharing an id would show one branch's children under the other's
  // parent row.
  return [p.bu ?? '', p.bt ?? '', p.sp ?? '', p.customer ?? ''].join('\u001f')
}

export function ReceivablesMatrix({
  api,
  viewAs = null,
  includeClosed = false,
  // Defaults to USD with the pages (23 Sep 2026); every parent passes it.
  currency = USD,
  filters = NO_RECV_FILTERS,
  exportRef,
}: {
  api: Api
  viewAs?: string | null
  includeClosed?: boolean
  currency?: Currency
  /** The page's standing filter bar. A change to it discards the whole tree,
      for the same reason a change of currency does: every number moves. */
  filters?: RecvFilters
  /** The page's card header renders the extract buttons; this hands it the
      builder for what is on screen now. Written every render, read at click. */
  exportRef?: { current: (() => ExportDoc | null) | null }
}) {
  /** Children by parent path id. Absent = never fetched. */
  const [children, setChildren] = useState<Map<string, MatrixNode[]>>(new Map())
  /** The new permission model answered (M8): a blank figure is withheld, and Partial marks a partly withheld one. */
  const [v2, setV2] = useState(false)
  const [expanded, setExpanded] = useState<Set<string>>(new Set())
  const [loading, setLoading] = useState<Set<string>>(new Set())
  const [failure, setFailure] = useState<ApiFailure | null>(null)
  /** A drill that failed, by the same child path id as `children`. Kept apart
      from `children` so a failure is never cached as an empty branch. */
  const [branchFailure, setBranchFailure] = useState<Map<string, ApiFailure>>(new Map())
  const [sortKey, setSortKey] = useState<keyof MatrixNode>(DEFAULT_SORT)
  const [sortDesc, setSortDesc] = useState(true)
  const memberRef = useRef<HTMLTableCellElement | null>(null)
  const layout = useGridLayout(GRID_ID, COLUMNS, memberRef)
  /* THE SORT RIDES INSIDE A SAVED VIEW. An unrecognised key -- a column since
     dropped, or one this reader may not be shown -- falls back to this grid's
     own order rather than sorting by nothing. See useSortMirror. */
  useSortMirror(GRID_ID, { key: sortKey, desc: sortDesc }, (s) => {
    const known = COLUMNS.some((c) => c.key === s.key)
    setSortKey(known ? (s.key as keyof MatrixNode) : DEFAULT_SORT)
    setSortDesc(known ? s.desc : true)
  })
  const shown = layout.columns
  const [dragKey, setDragKey] = useState<string | null>(null)
  const [overKey, setOverKey] = useState<string | null>(null)
  const [nonce, setNonce] = useState(0)

  const rootId = pathId({})

  // The GENERATION of the tree. Bumped whenever the tree is discarded (see the
  // reset effect), and captured by every request before it awaits: a response
  // from an older generation is dropped. Without this, flipping Local/USD or
  // View As while a fetch was in flight let the OLD currency's rows land in
  // the NEW tree under the same root id — figures in one unit rendered under a
  // header claiming another, and the spinner cleared as if all were well.
  const gen = useRef(0)

  const fetchLevel = useCallback(
    async (path: MatrixPath) => {
      const id = pathId(path)
      const g = gen.current
      const clearBranchFailure = () =>
        setBranchFailure((m) => {
          if (!m.has(id)) return m
          const next = new Map(m)
          next.delete(id)
          return next
        })
      setLoading((s) => new Set(s).add(id))
      clearBranchFailure() // a retry starts clean
      try {
        const { data: rows, vis } = await api.receivablesMatrix(path, { includeClosed, currency, filters })
        if (g !== gen.current) return // superseded — the tree it belonged to is gone
        setChildren((m) => new Map(m).set(id, rows))
        setV2(vis !== null)
        clearBranchFailure()
      } catch (e: unknown) {
        if (g !== gen.current) return
        // A failure while expanding one branch must not blank the whole report:
        // the rows already on screen are still true. Only a failure at the ROOT
        // replaces the page, because then there is nothing true left to show.
        const f = e instanceof ApiError ? e.failure : { kind: 'error' as const, title: 'Unexpected error.' }
        if (id === rootId) setFailure(f)
        else {
          // NOT cached as an empty child list. That drew an expanded chevron
          // over nothing, which reads as "this unit has no open receivables",
          // threw the correlation id away, and since toggle only fetches a
          // branch it has never cached, clicking again never retried. Collapse
          // it and keep the failure: FailureState renders under the row, and
          // the next expand fetches again.
          setExpanded((s) => {
            const next = new Set(s)
            next.delete(id)
            return next
          })
          setBranchFailure((m) => new Map(m).set(id, f))
        }
      } finally {
        // A superseded request must not clear the spinner either: the reset
        // effect already emptied `loading`, and the live request owns it now.
        if (g === gen.current)
          setLoading((s) => {
            const next = new Set(s)
            next.delete(id)
            return next
          })
      }
    },
    [api, viewAs, includeClosed, currency, filters, rootId],
  )

  // Whose eyes we are looking through, and whether closed items are in, both
  // change every number on screen — so the whole tree is discarded rather than
  // refreshed in place. Keeping expanded branches would leave one person's
  // subtotals under another person's parent row until each was re-fetched.
  useEffect(() => {
    gen.current += 1 // every request already in flight now belongs to a dead tree
    setChildren(new Map())
    setExpanded(new Set())
    setLoading(new Set())
    setFailure(null)
    setBranchFailure(new Map())
    void fetchLevel({})
  }, [fetchLevel, nonce])

  const toggle = useCallback(
    (row: Row) => {
      const cp = childPath(row.path, row.node)
      const id = pathId(cp)
      // The walk's cycle guard stops the RENDER looping, but this is the
      // interaction half of the same defect: when childPath cannot advance,
      // `id` names this row's OWN PARENT bucket, so toggling would collapse the
      // parent the row sits under. Such a row is a leaf — do nothing.
      if (id === pathId(row.path)) return
      setExpanded((s) => {
        const next = new Set(s)
        if (next.has(id)) next.delete(id)
        else next.add(id)
        return next
      })
      if (!children.has(id)) void fetchLevel(cp)
    },
    [children, fetchLevel],
  )

  const sortNodes = useCallback(
    (nodes: MatrixNode[]): MatrixNode[] => {
      const dir = sortDesc ? -1 : 1
      return [...nodes].sort((a, b) => {
        const av = a[sortKey]
        const bv = b[sortKey]
        // Masked measures sort last in both directions. They are not "smallest";
        // they are unknown, and letting them lead a descending sort would put
        // the rows the caller can say least about at the top of the report.
        if (av === null && bv === null) return (a.nodeKey ?? '').localeCompare(b.nodeKey ?? '')
        if (av === null) return 1
        if (bv === null) return -1
        if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * dir
        return String(av).localeCompare(String(bv)) * dir
      })
    },
    [sortKey, sortDesc],
  )

  /** Depth-first flatten of the expanded tree — what actually gets rendered. */
  const rows = useMemo(() => {
    const out: Row[] = []
    const walk = (path: MatrixPath, depth: number, seen: Set<string>) => {
      const nodes = children.get(pathId(path))
      if (!nodes) return
      const sorted = sortNodes(nodes)
      for (let index = 0; index < sorted.length; index++) {
        const node = sorted[index]
        const cp = childPath(path, node)
        const id = pathId(cp)
        // Unique per row: the parent bucket (pathId(path)) + the node's own key +
        // its position, so sibling documents — which share `id` — never collide.
        const key = `${pathId(path)}${node.nodeKey ?? ''}#${index}`
        // Expandable only if childPath actually advanced. When it did not, `id`
        // names this row's own parent bucket: the chevron would be inert and
        // toggling it would collapse the parent, so do not draw one.
        const expandable = node.hasChildren && id !== pathId(path)
        out.push({ node, path, id, key, depth, expandable })
        // Recurse ONLY into a child whose pathId is not already an ancestor.
        // childPath's default branch returns the parent unchanged (a Document
        // leaf, or a level the switch does not recognise), so pathId(cp) can
        // equal pathId(path). A bad hasChildren=true on such a node, once
        // expanded, made the old code call walk on the SAME bucket forever and
        // blow the stack, blanking the whole report. `seen` (ancestors incl.
        // self) stops it: a node that cannot go deeper is a leaf here, whatever
        // the server claims.
        if (node.hasChildren && expanded.has(id) && !seen.has(id)) walk(cp, depth + 1, new Set(seen).add(id))
      }
    }
    walk({}, 0, new Set([pathId({})]))
    return out
  }, [children, expanded, sortNodes])

  const root = children.get(rootId)

  /**
   * The grand total, summed from the top level.
   *
   * Null-if-any-null, not treat-null-as-zero: if one business unit's measure is
   * masked, the total across all of them is genuinely unknown, and printing the
   * sum of the visible ones would be a number that reconciles to nothing.
   *
   * UNDER THE NEW MODEL a measure is masked per piece of access, so a unit can
   * show it while its neighbour withholds it: the total is the sum of what shows,
   * marked as partly withheld, and withheld only when no unit shows it (M8).
   */
  const grandTotal = useMemo(() => {
    if (!root || root.length === 0) return null
    const t: Partial<Record<keyof MatrixNode, number | null>> = {}
    for (const c of COLUMNS) {
      const vals = root.map((n) => n[c.key] as number | null)
      t[c.key] = v2
        ? sumShown(root, [String(c.key), (n) => n[c.key] as number | null]).value
        : vals.some((v) => v === null)
          ? null
          : vals.reduce<number>((s, v) => s + (v ?? 0), 0)
    }
    return t
  }, [root, v2])
  /** Under the new model, the totals that leave a withheld or partly withheld unit out. */
  const totalPartial = useMemo(() => {
    const out = new Set<string>()
    if (!v2 || !root) return out
    for (const c of COLUMNS) {
      if (sumShown(root, [String(c.key), (n) => n[c.key] as number | null]).partial) out.add(String(c.key))
    }
    return out
  }, [root, v2])

  /**
   * The unit for the TOTAL row: a code only when every root node agrees on one.
   *
   * The grand total is a sum across the whole visible book. If one root node is
   * Saudi and another Tunisian, that sum has no unit — and the total row is
   * exactly where a wrong symbol does the most damage, because it is the figure
   * people quote.
   */
  const totalCode = useMemo(() => {
    if (!root || root.length === 0) return null
    const codes = new Set(root.map((n) => n.currencyCode))
    return codes.size === 1 ? [...codes][0] : null
  }, [root])

  if (failure) return <FailureState failure={failure} onRetry={() => setNonce((n) => n + 1)} />

  const rootLoading = loading.has(rootId)
  const caption = root && root.length > 0 ? LEVEL_CAPTION[root[0].groupedBy] : 'Business Unit'

  /* THE EXTRACT — the matrix exactly as expanded on screen: the same visible
     rows in the same order, children indented under their parents, masked
     measures null (an em dash there, never zero); the Total row rides along
     whenever it is drawn. */
  const buildDoc = (): ExportDoc | null => {
    if (rootLoading || rows.length === 0) return null
    const sortCol = COLUMNS.find((c) => c.key === sortKey)
    return {
      report: 'Receivables',
      page: 'Aging Dashboard',
      title: 'Receivables aging matrix',
      subtitle: 'By business unit, team, salesperson, customer and document — as expanded on screen.',
      context: [
        includeClosed ? 'Scope: open and closed items' : 'Scope: open items only',
        `Currency: ${currencyNote(currency)}`,
        `Sorted by ${sortCol?.header ?? String(sortKey)}, ${sortDesc ? 'descending' : 'ascending'} — applied within each level`,
        'Rows: as drilled on screen — a child level appears only where its parent was expanded',
      ],
      privileged: viewAs,
      currency,
      tables: [
        {
          columns: [
            { header: caption },
            ...shown.map((c) => ({ header: c.header, kind: 'money' as const })),
          ],
          rows: [
            ...rows.map<ExportRow>((r) => ({
              indent: r.depth,
              bold: r.depth === 0,
              cells: [r.node.nodeKey || '(blank)', ...shown.map((c) => {
                const v = r.node[c.key] as number | null
                return v2 && v === null ? WITHHELD : v
              })],
            })),
            ...(grandTotal
              ? [
                  {
                    bold: true,
                    cells: ['Total', ...shown.map((c) => grandTotal[c.key] ?? (v2 ? WITHHELD : null))],
                  } satisfies ExportRow,
                ]
              : []),
          ],
          note: [
            !isConverted(currency)
              ? 'Each row is stated in its own booked currency — the unit rides the row on screen and is not stamped on the raw figures here. A total across currencies has no single unit.'
              : undefined,
            v2 && (totalPartial.size > 0 || rows.some((r) => partialOf(r.node).size > 0)) ? PARTIAL_NOTE : undefined,
          ].filter(Boolean).join(' ') || undefined,
        },
      ],
    }
  }

  // Hand the current builder to the page's card header. The header lives above
  // the scroll region, so the buttons still never ride away with a sideways-
  // scrolled grid — the property the old dedicated row existed to guarantee.
  if (exportRef) exportRef.current = buildDoc

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {/* Portals into the card header beside Full screen; no height is spent
          here. The measure columns are the reader's — the member column beside
          them is not, and stays where it is. */}
      <GridChrome api={layout} />
      {/* The matrix is 12 columns wide and will not fit a laptop. It scrolls
          INSIDE this container so the page itself never scrolls sideways, and the
          row-header column is sticky so a document number stays attached to its
          figures at the right-hand end.

          overflow-AUTO, not overflow-x-auto, plus flex-1 min-h-0 so it takes the
          height the Card gives it. This is now the only scrolling region on the
          page: the title, tabs, KPI strip and card header all stay put, and the
          sticky column headers below finally have something to stick to. */}
      <div className="scroll-slim min-h-0 flex-1 overflow-auto">
      <table
        // border-SEPARATE, on purpose. With border-collapse the line between two
        // rows is a shared edge owned by neither cell, and the sticky row-header
        // cell is its own compositor layer: at fractional row heights it sits a
        // sub-pixel off the edge on alternate rows and its opaque background
        // paints the line out — the "missing lines between business units" a
        // user reported. Each cell now draws its OWN bottom border, which an
        // element always paints above its own background, so nothing can cover it.
        className="report-table w-full min-w-[1100px] border-separate border-spacing-0"
        style={{ fontSize: VALUE_PX }}
      >
        <thead>
          <tr>
            {/* Title alignment Left, from the report's columnHeaders object;
                the measure headers are Center. */}
            <th
              ref={memberRef}
              scope="col"
              style={{ fontSize: HEADER_PX, lineHeight: HEADER_LH }}
              /* 300px is the frozen column on a laptop. On a phone it was 300
                 of 366px and left no room for a single figure beside it, so
                 under a 768px content box it drops to 160px and long names
                 wrap instead. */
              className="sticky left-0 top-0 z-30 min-w-[300px] border-b border-(--color-line-strong) px-4 py-2.5 text-center font-semibold text-(--color-ink) @max-3xl:min-w-[10rem]"
            >
              {/* The caption names the level you are expanded to, which is what
                  the `member` row documents: why the salesperson level can be
                  absent entirely rather than blanked. */}
              <Explain surface="matrix" measureKey="member">
                {caption}
              </Explain>
            </th>
            {shown.map((c) => {
              const pin = layout.pinOf(c.key)
              const w = layout.widthOf(c.key) ?? c.width
              return (
              <th
                key={String(c.key)}
                ref={layout.headerRef(c.key)}
                scope="col"
                aria-sort={sortKey === c.key ? (sortDesc ? 'descending' : 'ascending') : 'none'}
                draggable
                onDragStart={(e) => { e.dataTransfer.effectAllowed = 'move'; setDragKey(c.key) }}
                onDragOver={(e) => { if (!dragKey || dragKey === c.key) return; e.preventDefault(); setOverKey(c.key) }}
                onDrop={(e) => {
                  e.preventDefault()
                  if (dragKey && dragKey !== c.key) layout.move(dragKey, layout.indexOf(c.key))
                  setDragKey(null); setOverKey(null)
                }}
                onDragEnd={() => { setDragKey(null); setOverKey(null) }}
                /* A FLOOR, NOT A CAP (4 Oct 2026): every column here is a
                   figure, and one wider than its width -- a local-currency
                   amount, say -- widens the column instead of being cut to
                   "…". See rule 1 in gridLayout.tsx. */
                style={{
                  fontSize: HEADER_PX, width: w, minWidth: w,
                  /* Pins start AFTER the member column, whose 300px the hook
                     measures — otherwise the first pinned measure slides under
                     it and reads as the member column's own figure. */
                  left: pin === 'left' ? layout.offsetOf(c.key) : undefined,
                  right: pin === 'right' ? layout.offsetOf(c.key) : undefined,
                }}
                className={`relative sticky top-0 border-b border-l border-(--color-line) px-3 py-2.5 text-center font-semibold text-(--color-ink) ${
                  pin ? 'z-30' : 'z-20'
                } ${overKey === c.key ? 'bg-(--color-navy-100)' : ''} ${dragKey === c.key ? 'opacity-50' : ''}`}
              >
                <span data-head-label className="inline-block whitespace-nowrap">
                <button
                  type="button"
                  onClick={() => {
                    if (sortKey === c.key) setSortDesc((d) => !d)
                    else {
                      setSortKey(c.key)
                      setSortDesc(true)
                    }
                  }}
                  className="whitespace-nowrap text-right transition-colors hover:text-(--color-accent)"
                  title={`Sort by ${c.header}`}
                >
                  {c.header}
                  {sortKey === c.key && <span aria-hidden> {sortDesc ? '▼' : '▲'}</span>}
                </button>
                {/* A MARK BESIDE, NOT A WRAPPER AROUND. These headers already
                    sort, and a button inside a button is invalid HTML that the
                    browser silently repairs by moving the inner one out.
                    Sorting keeps the click. */}
                <ExplainMark surface="matrix" measureKey={String(c.key)} label={c.header} />
                </span>
                <ResizeHandle
                  label={c.header}
                  widthOf={() => layout.headerWidth(c.key)}
                  minWidth={() => layout.minWidthOf(c.key)}
                  onResize={(px) => layout.resize(c.key, px)}
                  onAutoFit={() => layout.autoFit(c.key)}
                />
              </th>
            )})}
          </tr>
        </thead>

        <tbody>
          {rootLoading && (
            <tr>
              <td colSpan={shown.length + 1} className="px-3 py-10">
                {/* Mounted in a table cell, but it renders at the centre of the
                    screen like every other loader — BrandLoader is fixed, so
                    this cell only decides WHEN it shows, never where. The row
                    keeps the table from collapsing while the body is empty.

                    rootLoading covers a refetch as well as the first load, so
                    changing the currency shows it too. */}
                <Loading label="Loading receivables…" />
              </td>
            </tr>
          )}

          {!rootLoading && root && root.length === 0 && (
            <tr>
              <td colSpan={shown.length + 1} className="px-3 py-10 text-center text-(--color-ink-3)">
                {viewAs
                  ? `${viewAs} has no open receivables in the data they can see.`
                  : 'No open receivables in the data you can see.'}
              </td>
            </tr>
          )}

          {rows.map((row, i) => {
            // Only an expandable row owns its `id`; a leaf's id is its parent's bucket.
            const drillFailure = row.expandable ? branchFailure.get(row.id) : undefined
            return (
              <Fragment key={row.key}>
                <MatrixRow
                  row={row}
                  first={i === 0}
                  expanded={expanded.has(row.id)}
                  loading={loading.has(row.id)}
                  onToggle={() => toggle(row)}
                  currency={currency}
                  shown={shown}
                  layout={layout}
                  v2={v2}
                />
                {/* The same FailureState the root uses, scoped to this branch so
                    the rows already on screen stay. Sticky-left so it is not
                    stranded off screen on a sideways-scrolled matrix. */}
                {drillFailure && (
                  <tr>
                    <td colSpan={shown.length + 1} className="px-3 pb-4">
                      <div className="sticky left-3 max-w-2xl">
                        <FailureState failure={drillFailure} onRetry={() => toggle(row)} />
                      </div>
                    </td>
                  </tr>
                )}
              </Fragment>
            )
          })}
        </tbody>

        {grandTotal && (
          <tfoot>
            {/* The rule above the total lives on the CELLS, not the row: a row
                border does not render in border-separate mode. */}
            <tr className="bg-(--color-surface-3) font-semibold">
              <th
                scope="row"
                style={{ fontSize: HEADER_PX, lineHeight: HEADER_LH }}
                className="sticky left-0 z-10 border-t-2 border-(--color-line-strong) bg-(--color-surface-3) px-4 py-2.5 text-left text-(--color-ink)"
              >
                Total
              </th>
              {shown.map((c) => {
                const pin = layout.pinOf(c.key)
                const w = layout.widthOf(c.key) ?? c.width
                return (
                  <td
                    key={String(c.key)}
                    style={{
                      width: w,
                      left: pin === 'left' ? layout.offsetOf(c.key) : undefined,
                      right: pin === 'right' ? layout.offsetOf(c.key) : undefined,
                    }}
                    className={`num whitespace-nowrap border-l border-t-2 border-l-(--color-line) border-t-(--color-line-strong) px-3 py-2.5 font-semibold text-(--color-ink) ${
                      pin ? 'sticky z-[2] bg-(--color-surface-3)' : ''
                    }`}
                  >
                    {measure(grandTotal[c.key] ?? null, currency, totalCode, v2, totalPartial.has(String(c.key)))}
                  </td>
                )
              })}
            </tr>
          </tfoot>
        )}
      </table>
      </div>
    </div>
  )
}

function MatrixRow({
  row,
  first,
  expanded,
  loading,
  onToggle,
  currency,
  shown,
  layout,
  v2,
}: {
  row: Row
  /** The new permission model answered (M8). */
  v2: boolean
  /* The row renders the measure cells, so it needs the same layout the header
     used — otherwise header, body and total would disagree about which column
     is which the moment a reader moves one. */
  shown: MatrixColumn[]
  layout: MatrixLayout
  /** The first body row draws no top rule — the sticky header already owns that line. */
  first: boolean
  expanded: boolean
  loading: boolean
  onToggle: () => void
  /* Needed to render the unit. The CODE comes from the row itself (the database
     nulls it when the node mixes currencies); this only says whether we are in
     dollars or in local money. */
  currency: Currency
}) {
  const { node, depth, expandable } = row
  // A salesperson's name can itself leave withheld rows out (Partial's nodeKey).
  const partial = partialOf(node)
  const label = node.nodeKey ?? ''
  // The separator is the TOP border of this row, not the bottom border of the
  // one above. Sibling sticky cells are composited layers painted in DOM order,
  // so a later row's layer — snapped to whole device pixels — can land over
  // the row above's bottom edge and cover a line drawn there. Nothing paints
  // over a layer's own top edge. (border-separate; a <tr> border would not draw.)
  const sep = first ? '' : 'border-t'

  return (
    <tr className="group transition-colors hover:bg-(--color-navy-50)">
      <th
        scope="row"
        // Stepped layout: ONE row-header column, indented per level. Not a
        // column per level — that is Power BI's "Outline" layout, and this
        // report is set to Compact.
        style={{ paddingLeft: 12 + depth * INDENT_PX, fontSize: HEADER_PX, lineHeight: HEADER_LH }}
        /* THE HOVER HAS TO BE REPEATED HERE BY HAND.
           The row paints its highlight on the <tr>, but this cell is frozen —
           a positioned layer with an opaque background of its own, composited
           ABOVE its own row. So the one column that never scrolls away was the
           one column the highlight never reached, and on a matrix 2,089px wide
           where only five of twelve columns fit, tracking a figure back to its
           owner is the whole job. group-hover puts the tint on the cell that
           has to carry it. */
        className={`sticky left-0 z-10 ${sep} border-(--color-line) bg-(--color-surface) py-2 pr-4 text-left font-semibold text-(--color-ink) transition-colors group-hover:bg-(--color-navy-50)`}
      >
        {expandable ? (
          <button
            type="button"
            onClick={onToggle}
            aria-expanded={expanded}
            className="flex items-center gap-1.5 text-left hover:text-(--color-accent)"
          >
            <span aria-hidden className="inline-block w-3 shrink-0 text-[10px] text-(--color-ink-3)">
              {loading ? '·' : expanded ? '▼' : '▶'}
            </span>
            {markPartial(partial.has('nodeKey'), label || <span className="font-normal italic text-(--color-ink-3)">(blank)</span>)}
          </button>
        ) : (
          <span className="flex items-center gap-1.5">
            <span aria-hidden className="inline-block w-3 shrink-0" />
            {markPartial(partial.has('nodeKey'), label || <span className="font-normal italic text-(--color-ink-3)">(blank)</span>)}
          </span>
        )}
      </th>

      {shown.map((c) => {
        const pin = layout.pinOf(c.key)
        const w = layout.widthOf(c.key) ?? c.width
        return (
          <td
            key={String(c.key)}
            // A figure is never cut: no cap, no ellipsis. The width is a floor.
            style={{
              width: w,
              left: pin === 'left' ? layout.offsetOf(c.key) : undefined,
              right: pin === 'right' ? layout.offsetOf(c.key) : undefined,
            }}
            className={`num ${sep} whitespace-nowrap border-l border-(--color-line) px-3 py-2 text-(--color-ink) ${
              pin ? 'sticky z-[1] bg-(--color-surface) group-hover:bg-(--color-navy-50)' : ''
            }`}
          >
            {measure(node[c.key] as number | null, currency, node.currencyCode, v2, partial.has(String(c.key)))}
          </td>
        )
      })}
    </tr>
  )
}

/**
 * A measure the caller may not see arrives null and renders as an em dash.
 *
 * Zero would be indistinguishable from a real zero balance and would be acted
 * on. This is the one formatting rule in the file that is not copied from the
 * Power BI report, because the Power BI report had no masked measures to render.
 */
/**
 * The unit travels with the ROW, not with the table. One node can be a single
 * company (SAR 1,234) while its sibling spans three and carries no code at all.
 * The database decides which by returning NULL for CurrencyCode on a node whose
 * children disagree, so this never has to guess.
 */
function measure(v: number | null, currency: Currency, code?: string | null, v2 = false, partial = false): ReactNode {
  // Under the new model a blank measure is withheld, and a partial one keeps its value and the mark (M8).
  if (v === null) return v2 ? <Withheld /> : HIDDEN
  return markPartial(partial, money(v, currency, code))
}
